<script lang="ts" setup generic="Key, Item">
import type { Density } from 'vuetify/lib/composables/density.mjs'
import { mdiBookOpenVariant, mdiBookshelf, mdiTrashCan, mdiTrashCanOutline, mdiDeleteEmpty } from '@mdi/js'
import { useVModel } from '@vueuse/core'
import { computed, ref, inject } from 'vue'
import Shortcut from '../../../app/components/util/Shortcut.vue'
import { useStateScope } from '../../../app/helper/shortcut.js'
import { getDensityClass, getDensityIconSize, getItemChildren, getItemError, getItemLoading, getItemTitle, getItemValue, getStyle, hasChildren, UTreeDataSymbol } from './utree.js'

interface UTreeLevelProps {
  groupItem: Item
  items: Array<Item>
  selected?: Array<Key>
  opened?: Array<Key>
  loadChildren?: (item: Item) => Promise<void>
  itemFilter?: (item: Item) => boolean
  level: number
  density?: Density
  scrollTo?: (el: HTMLElement) => void
}

const props = defineProps<UTreeLevelProps>()
const emits = defineEmits(['update:selected', 'update:opened', 'click:open', 'contextmenu'])
const route = useRoute('client-view-path')
const router = useRouter()

const groupItem = computed(() => props.groupItem)
const selected = useVModel(props, 'selected', emits)
const opened = useVModel(props, 'opened', emits)

const itemsFiltered = computed(() => {
  if (!props.itemFilter)
    return props.items
  return props.items.filter(props.itemFilter)
})

const { t } = useI18n({ useScope: 'local' })

const loadingChildren = ref(false)
const childrenLoaded = ref(false)

const uTreeData = inject(UTreeDataSymbol)

const keys = computed(() => {
  return uTreeData?.value?.keys ?? { itemTitle: 'title', itemValue: 'value', itemChildren: 'children', itemError: 'error', itemType: 'type', itemLoading: 'loading' }
})

const type = computed(() => groupItem.value[keys.value.itemType as keyof Item])

const icons = computed(() => {
  if (uTreeData?.value)
    return uTreeData.value.icons
  return {}
})

function isItemActive(item: Item): boolean {
  if (selected.value === undefined)
    return false
  const idx = selected.value.find(v => v === getItemValue(item, keys.value))
  return idx !== undefined
}

function isFolderInActivePath(item: Item): boolean {
  if (selected.value === undefined || selected.value.length === 0)
    return false
  const selectedKey = selected.value[0]
  if (typeof selectedKey !== 'string')
    return false
  const itemKey = getItemValue(item, keys.value)
  if (typeof itemKey !== 'string')
    return false
  return selectedKey.startsWith(`${itemKey}/`)
}

async function fetchChildren(item: Item): Promise<void> {
  const children = getItemChildren(item, keys.value)
  if (children !== undefined && children.length === 0 && props.loadChildren !== undefined) {
    loadingChildren.value = true
    await props.loadChildren(item)
    loadingChildren.value = false
    uTreeData?.value?.fillChildren(item)
  }
  childrenLoaded.value = true
}

function closeChildren(children: Key[], folderSet: Set<Key>): void {
  for (const child of children) {
    if (uTreeData?.value) {
      const listChildren = uTreeData.value.children.value.get(child) as Key[]
      if (listChildren) {
        closeChildren(listChildren, folderSet)
      }
    }
    folderSet.delete(child)
  }
}

function isFolderOpen(item: Item): boolean {
  const key = getItemValue(item, keys.value)
  return opened.value.findIndex(v => v === key) !== -1
}

async function folderClick(item: Item, isOpen: boolean): Promise<void> {
  const folderSet = new Set(opened.value)
  if (isOpen) { // folder is already open, close it
    folderSet.delete(getItemValue(item, keys.value))
    if (uTreeData?.value) {
      const listChildren = uTreeData.value.children.value.get(getItemValue(item, keys.value)) as Key[]
      if (listChildren) {
        closeChildren(listChildren, folderSet)
      }
    }
    if ((selected.value?.[0] as string)?.startsWith(getItemValue(item, keys.value))) {
      folderSelect(item)
    }
  }
  else {
    folderSet.add(getItemValue(item, keys.value))
    await fetchChildren(item)
    if (getItemError(item, keys.value)) {
      folderSet.delete(getItemValue(item, keys.value)) // failed to fetch the folder, close it
      return
    }
  }
  opened.value = Array.from(folderSet.values()) as any
  emits('click:open', { id: getItemValue(item, keys.value), value: !isOpen, path: [] })
}

const doubleClickTime = 250 // ms double click

// on every click start a tracker which waits for the double click timer
// if there is another click withing that timelimit then a double click is performed
// otherwise a single click
let doubleClickTrack = false
let timeout: NodeJS.Timeout | null = null

function folderSelect(item: Item): void {
  if (isItemActive(item)) {
    const isOnViewPath = !!((route.params as Record<string, unknown>).path as string[] | undefined)?.length
    if (isOnViewPath) {
      // Already selected while viewing content: toggle expand/collapse on full-element click
      folderClick(item, isFolderOpen(item))
    }
    else {
      // Already selected but main frame shows something else (e.g. overview after navigation):
      // force re-emit to trigger navigation even if the selected value hasn't changed
      const val = [getItemValue(item, keys.value)] as any
      selected.value = val
      emits('update:selected', val)
    }
    return
  }

  if (doubleClickTrack) {
    doubleClickTrack = false
    clearTimeout(timeout!)
    folderClick(item, isFolderOpen(item))
  }
  else {
    doubleClickTrack = true
    timeout = setTimeout(() => {
      doubleClickTrack = false
      selected.value = [getItemValue(item, keys.value)] as any
    }, doubleClickTime)
  }
}

function focusLevelAbove() {
  const activeEl = document.activeElement as HTMLElement | null
  if (activeEl) {
    const levelEl = activeEl.closest('.v-list-group--open') as HTMLElement | null
    if (levelEl) {
      const levelItem = levelEl.querySelector('.v-list-item') as HTMLElement | null
      if (levelItem) {
        props.scrollTo?.(levelItem)
        // levelItem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
        levelItem.focus()
      }
    }
  }
}

function folderOpen(item: Item, open: boolean): void {
  const folderSet = new Set(opened.value)
  if (open) {
    folderSet.add(getItemValue(item, keys.value))
    fetchChildren(item)
  }
  else {
    if (!isFolderOpen(item)) {
      focusLevelAbove()
      return
    }
    folderSet.delete(getItemValue(item, keys.value))
  }
  opened.value = Array.from(folderSet.values()) as any
}

function shortManualName(s?: string): string | undefined {
  if (!s)
    return undefined
  const manual = '.manual'
  if (s.endsWith(manual)) {
    return s.slice(0, -manual.length)
  }
  if (s === 'binfolder.bin') {
    return t('bin')
  }
  return s
}

function getRootErrorIcon(): string | undefined {
  if (icons.value.rootError)
    return icons.value.rootError
  return icons.value.error
}

const densityClass = computed(() => props.density ? getDensityClass(props.density) : 'density-default')
const iconSize = computed(() => props.density ? getDensityIconSize(props.density) : undefined)

function handleContextMenu(current: string): void {
  const path = current.split('/')
  const routeData = router.resolve({ name: 'client-view-path', params: { client: route.params.client, path } })
  window.open(routeData.href, '_blank')
}
// const showFileInformation = useLocalStorage('showFileInformation', true)

const state = useStateScope()
</script>

<template>
  <v-list-group
    :value="getItemValue(groupItem, keys)"
    class="color"
  >
    <template #activator>
      <span>
        <v-list-item
          class="level"
          :class="densityClass"
          :density="density"
          :title="`${shortManualName(getItemTitle(props.groupItem, keys))}`"
          :value="getItemValue(props.groupItem, keys)"
          :active="isItemActive(props.groupItem)"
          slim
          :style="getStyle(level - 1)"
          @click.stop="folderSelect(groupItem)"
          @click.middle.prevent="handleContextMenu(getItemValue(props.groupItem, keys))"
          @keydown.right.prevent="() => folderOpen(props.groupItem, true)"
          @keydown.left.prevent="() => folderOpen(props.groupItem, false)"
        >
          <template #prepend="{ isOpen }">
            <div :class="densityClass" @click.stop="folderClick(groupItem, isOpen)">

              <template v-if="!getItemLoading(props.groupItem, keys)">
                <v-icon
                  v-if="getItemError(groupItem, keys)"
                  :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="level === 1 ? getRootErrorIcon() : icons.error"
                  class="icon"
                  :size="iconSize"
                />
                <template v-else-if="type === 'directory'">
                  <v-icon
                    v-if="level === 1 && icons.root"
                    :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="isOpen ? icons.rootOpened : icons.root"
                    class="icon"
                    :size="iconSize"
                  />
                  <v-icon
                    v-else-if="!childrenLoaded || getItemChildren(groupItem, keys).length > 0"
                    :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="isOpen ? icons.collapse : icons.expand"
                    class="icon"
                    :size="iconSize"
                  />
                  <v-icon v-else :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="icons.empty" :size="iconSize" />
                </template>
                <template v-else-if="type === 'bin'">
                  <v-icon v-if="!childrenLoaded || getItemChildren(groupItem, keys).length > 0" :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="isOpen ? mdiTrashCanOutline : mdiTrashCan" :size="iconSize" />
                  <v-icon v-else :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="mdiDeleteEmpty" :size="iconSize" />
                </template>
                <v-icon
                  v-else-if="type === 'manual'"
                  :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="isOpen ? mdiBookOpenVariant : mdiBookshelf"
                  class="icon"
                  :size="iconSize"
                />
              </template>
              <v-icon
                v-else
                class="rotating-icon"
                :color="isFolderInActivePath(groupItem) ? 'primary' : undefined" :icon="icons.load"
              />
            </div>
          </template>
          <template #subtitle>
            <!-- <div class="no-compact-style ml-2">
              <div class="d-flex" style="gap:4px;">
                <span :class="densityClass">
                  <v-chip density="comfortable" size="x-small">{{ totalFolderCount ? (groupItem as any).totalElementsInside : (groupItem as any).elementsInside }} {{ t('elements') }}</v-chip>
                </span>
                <span v-if="showFileInformation" :class="densityClass">
                  <v-chip density="comfortable" size="x-small" :prepend-icon="mdiFileChart">{{ filesize((groupItem as any).size ?? 0, { standard: "jedec" }) }}</v-chip>
                </span>
              </div>
            </div> -->
          </template>

          <template #append>
            <slot name="append" :active="isItemActive(props.groupItem)" :item="props.groupItem" />
            <Shortcut v-if="isItemActive(props.groupItem) && !state?.treeFocus.value" keys="Alt+A" only-display />
          </template>
        </v-list-item>
      </span>
    </template>
    <template
      v-for="(item, i) of itemsFiltered" :key="i"
    >
      <UTreeLevel
        v-if="hasChildren(getItemChildren(item, keys))"
        :key="i"
        v-model:selected="selected"
        v-model:opened="opened"
        class="navigationBorder"
        :load-children="loadChildren"
        :item-filter="itemFilter"
        :group-item="item"
        :items="getItemChildren(item, keys)"
        :level="level + 1"
        :density="density"
      >
        <template #item="{ it, style }">
          <slot name="item" v-bind="{ it, style }">
            <span class="item">
              <v-list-item
                :title="getItemTitle(it, keys)"
                :value="getItemValue(it, keys)"
                :style="style"
                :class="densityClass"
                :density="density"
              />
            </span>
          </slot>
        </template>
      </UTreeLevel>
      <div v-else class="navigationBorder">
        <slot name="item" v-bind="{ it: item, style: getStyle(level) }">
          <span class="item">
            <v-list-item
              :key="i"
              :title="getItemTitle(item, keys)"
              :value="getItemValue(item, keys)"
              :style="getStyle(level)"
              :density="density"
              :class="densityClass"
            >
              <template #prepend>
                <div class="icon-container">
                  <slot name="prepend" />
                </div>
              </template>
            </v-list-item>
          </span>
        </slot>
      </div>
    </template>
  </v-list-group>
</template>

<style scoped>
@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.rotating-icon {
  animation: rotate 0.5s linear infinite;
}

/*
--------------------------------------------
In case of changes see TreeView.vue as well
*/

.level :deep(.v-list-group__items), :deep(.v-list-item){
  padding-inline-start:12px !important;
}

.level :deep(.v-list-item__prepend){
  height: 100% !important;
}

.density-default :deep(.v-list-item-title) {
  padding-left: 12px;
}

.density-comfortable :deep(.v-list-item-title) {
  padding-left: 8px;
}

.density-compact :deep(.v-list-item-title) {
  padding-left: 4px;
}

.item .density-compact {
  display: flex;
  align-items: center;
  justify-content: left;
  padding-top: 2px;
  padding-bottom: 2px;
  min-height: 35px;
  height: 35px;
}

.item .density-comfortable  {
  display: flex;
  align-items: center;
  justify-content: left;
  padding-top: 4px;
  padding-bottom: 4px;
  min-height: 40px;
  height: 40px;
}

.item .density-default {
  display: flex;
  align-items: center;
  justify-content: left;
  padding-top: 4px;
  padding-bottom: 4px;
  min-height: 45px;
  height: 45px;
}

.navigationBorder{
  margin-inline-start: 24px !important;
  border-left: 1px solid rgba(var(--v-theme-primary),0.6);
}

.icon-container{
  display: flex;
  height:100%;
  width: 100%;
  align-items: center;
  cursor: pointer;

  .item-amount-display {
    position: absolute;
    left:0;
    right:0;
    top:0;
    bottom:0;
    z-index: 1;
    display: flex;
    justify-items: center;
    align-items: center;
    /* margin-top: -4px; */
    /* margin-right: -8px; */
    margin-top: 8px;
    flex-direction: column;
    span {
      display: block;
      flex: 0;

    }
  }
}

.icon{
  transition: transform 0.3s;
}

.icon-container:hover .icon{
  transform: scale(1.2);
}

.color  :deep(.v-list-item--active){
  color: rgba(var(--v-theme-primary),0.7);
}

.color  :deep(.v-list-item--active):hover{
  color: rgba(var(--v-theme-primary),1);
}
</style>

<i18n lang="yaml">
  de:
    bin: 'Papierkorb'
    elements: 'Elemente'
  en:
    bin: 'Recyclebin'
    elements: 'Elements'
</i18n>
